import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Dropdown, Modal, Button, Form } from 'react-bootstrap';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import ProductImage from '../ProductImage/ProductImage';
import { useSugestaoCompra } from '../../hooks/useSugestaoCompra';
import styles from './ProductCard.module.css';

// ─── Modal de edição da fórmula da sugestão ───
const SugestaoCompraModal = ({ show, onHide, codigo, descricao, calculo, solicitacoes = [], onSalvar, saving, errorMsg }) => {
    const defaultLead     = calculo?.lead_time ?? 15;
    const defaultCobertura= calculo?.cobertura_dias ?? 30;
    const defaultBufferPct= calculo?.buffer_pct ?? 30;

    const [leadTime, setLeadTime] = useState(String(defaultLead));
    const [cobertura, setCobertura] = useState(String(defaultCobertura));
    const [bufferPct, setBufferPct] = useState(String(defaultBufferPct));

    // Reset state quando o modal abre com produto novo
    useEffect(() => {
        if (show) {
            setLeadTime(String(calculo?.lead_time ?? 15));
            setCobertura(String(calculo?.cobertura_dias ?? 30));
            setBufferPct(String(calculo?.buffer_pct ?? 30));
        }
    }, [show, calculo?.lead_time, calculo?.cobertura_dias, calculo?.buffer_pct]);

    // Preview ao vivo: replica a fórmula do backend client-side
    const preview = useMemo(() => {
        const demanda = Number(calculo?.demanda_diaria) || 0;
        const estoque = Number(calculo?.estoque) || 0;
        const solic   = Number(calculo?.solicitacoes_abertas) || 0;
        const lead    = parseInt(leadTime, 10);
        const cob     = parseInt(cobertura, 10);
        const buf     = parseFloat(bufferPct);

        if (!Number.isFinite(lead) || !Number.isFinite(cob) || !Number.isFinite(buf)) {
            return { sugestao: null, valido: false, motivo: 'Valores inválidos' };
        }
        if (lead < 1 || lead > 180) return { sugestao: null, valido: false, motivo: 'Lead time entre 1 e 180' };
        if (cob < 1 || cob > 365)   return { sugestao: null, valido: false, motivo: 'Cobertura entre 1 e 365' };
        if (buf < 0 || buf > 200)   return { sugestao: null, valido: false, motivo: 'Buffer entre 0 e 200' };

        const bufferAbs = Math.ceil(demanda * lead * (buf / 100));
        const necessidade = demanda * (lead + cob) + bufferAbs;
        const disponivel = estoque + solic;
        const sugestao = Math.max(0, Math.ceil(necessidade - disponivel));
        return { sugestao, valido: true, buffer: bufferAbs, necessidade: Math.ceil(necessidade), disponivel };
    }, [leadTime, cobertura, bufferPct, calculo]);

    const handleSalvar = () => {
        if (!preview.valido) return;
        onSalvar({
            lead_time: parseInt(leadTime, 10),
            cobertura_dias: parseInt(cobertura, 10),
            buffer_pct: parseFloat(bufferPct)
        });
    };

    const handleResetar = () => {
        setLeadTime('15');
        setCobertura('30');
        setBufferPct('30');
    };

    return (
        <Modal show={show} onHide={onHide} centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: '1.1rem' }}>Editar Fórmula da Sugestão</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '14px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>#{codigo}</div>
                    <div>{descricao}</div>
                </div>

                <div style={{ marginBottom: '14px' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569', letterSpacing: '0.5px', marginBottom: '4px' }}>
                        DADOS AUTOMÁTICOS (não editáveis)
                    </div>
                    <div style={{ fontSize: '0.85rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
                        <span>Demanda diária:</span><strong>{calculo?.demanda_diaria}/dia</strong>
                        <span>Estoque atual:</span><strong>{calculo?.estoque}</strong>
                        <span>Pedidos em aberto:</span><strong>{calculo?.solicitacoes_abertas}</strong>
                    </div>
                </div>

                <div style={{ marginBottom: '14px' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569', letterSpacing: '0.5px', marginBottom: '8px' }}>
                        PARÂMETROS EDITÁVEIS
                    </div>
                    <Form.Group className="mb-2">
                        <Form.Label style={{ fontSize: '0.85rem', marginBottom: '2px' }}>
                            Lead time (dias até chegar do fornecedor)
                        </Form.Label>
                        <Form.Control
                            type="number"
                            min="1"
                            max="180"
                            value={leadTime}
                            onChange={(e) => setLeadTime(e.target.value)}
                            size="sm"
                        />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label style={{ fontSize: '0.85rem', marginBottom: '2px' }}>
                            Cobertura (dias de estoque após chegar)
                        </Form.Label>
                        <Form.Control
                            type="number"
                            min="1"
                            max="365"
                            value={cobertura}
                            onChange={(e) => setCobertura(e.target.value)}
                            size="sm"
                        />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label style={{ fontSize: '0.85rem', marginBottom: '2px' }}>
                            Buffer de segurança (%) — gordura sobre lead × demanda
                        </Form.Label>
                        <Form.Control
                            type="number"
                            min="0"
                            max="200"
                            step="5"
                            value={bufferPct}
                            onChange={(e) => setBufferPct(e.target.value)}
                            size="sm"
                        />
                    </Form.Group>
                </div>

                <div style={{
                    background: preview.valido ? (preview.sugestao > 0 ? '#eff6ff' : '#f0fdf4') : '#fef2f2',
                    border: `1px solid ${preview.valido ? (preview.sugestao > 0 ? '#bfdbfe' : '#bbf7d0') : '#fecaca'}`,
                    padding: '10px 12px',
                    borderRadius: '6px'
                }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569', letterSpacing: '0.5px', marginBottom: '4px' }}>
                        PREVIEW DO RESULTADO
                    </div>
                    {!preview.valido ? (
                        <div style={{ color: '#991b1b', fontSize: '0.85rem' }}>{preview.motivo}</div>
                    ) : (
                        <>
                            <div style={{
                                fontSize: '1.6rem',
                                fontWeight: 800,
                                color: preview.sugestao > 0 ? '#1d4ed8' : '#15803d',
                                lineHeight: 1
                            }}>
                                {preview.sugestao > 0 ? `${preview.sugestao} un` : 'Não comprar'}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#475569', marginTop: '6px' }}>
                                {calculo?.demanda_diaria}/dia × ({leadTime} + {cobertura})d = {Math.ceil(calculo?.demanda_diaria * (parseInt(leadTime,10) + parseInt(cobertura,10)))} +
                                {' '}{preview.buffer} buffer −
                                {' '}{preview.disponivel} disponível
                            </div>
                        </>
                    )}
                </div>

                {solicitacoes && solicitacoes.length > 0 && (
                    <div style={{ marginTop: '14px' }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569', letterSpacing: '0.5px', marginBottom: '6px' }}>
                            SOLICITAÇÕES DE COMPRA ABERTAS ({solicitacoes.length})
                        </div>
                        <div style={{ border: '1px solid #e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                            <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                                        <th style={{ padding: '6px 8px', fontWeight: 600 }}>Data</th>
                                        <th style={{ padding: '6px 8px', fontWeight: 600 }}>Fornecedor</th>
                                        <th style={{ padding: '6px 8px', fontWeight: 600 }}>Status</th>
                                        <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>Qtd</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {solicitacoes.map((s, i) => {
                                        const dt = s.data_solicitacao
                                            ? new Date(s.data_solicitacao).toLocaleDateString('pt-BR')
                                            : '—';
                                        return (
                                            <tr key={s.id || i} style={{ borderTop: i > 0 ? '1px solid #f1f5f9' : 'none' }}>
                                                <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>{dt}</td>
                                                <td style={{ padding: '5px 8px' }}>{s.fornecedor || '—'}</td>
                                                <td style={{ padding: '5px 8px' }}>
                                                    <span style={{
                                                        fontSize: '0.7rem',
                                                        padding: '2px 6px',
                                                        borderRadius: '3px',
                                                        background: s.status === 'aprovado' ? '#dbeafe' : '#fef3c7',
                                                        color: s.status === 'aprovado' ? '#1e40af' : '#92400e'
                                                    }}>{s.status}</span>
                                                </td>
                                                <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600 }}>{s.quantidade}</td>
                                            </tr>
                                        );
                                    })}
                                    <tr style={{ background: '#f8fafc', fontWeight: 700, borderTop: '2px solid #cbd5e1' }}>
                                        <td colSpan={3} style={{ padding: '6px 8px' }}>TOTAL EM PEDIDO</td>
                                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                                            {solicitacoes.reduce((s, x) => s + (Number(x.quantidade) || 0), 0)} un
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '4px', fontStyle: 'italic' }}>
                            Esses valores já estão descontados do cálculo da sugestão.
                        </div>
                    </div>
                )}

                {errorMsg && (
                    <div style={{ marginTop: '10px', color: '#dc2626', fontSize: '0.85rem' }}>
                        {errorMsg}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer style={{ justifyContent: 'space-between' }}>
                <Button variant="outline-secondary" size="sm" onClick={handleResetar} disabled={saving}>
                    Resetar defaults
                </Button>
                <div>
                    <Button variant="secondary" size="sm" onClick={onHide} disabled={saving} className="me-2">
                        Cancelar
                    </Button>
                    <Button variant="primary" size="sm" onClick={handleSalvar} disabled={saving || !preview.valido}>
                        {saving ? 'Salvando...' : 'Salvar override'}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

const SugestaoCompraBloco = ({ codigo, fornecedor, loja, estoque, precoCusto, precoVenda, descricao, onCloseTooltip }) => {
    const { data, isLoading, isError } = useSugestaoCompra({
        codigo, fornecedor, loja, estoque, precoCusto, precoVenda
    });

    const [showModal, setShowModal] = useState(false);
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async (overrides) => {
            const { data: resp } = await axios.post(
                `/api/produtos/sugestao-compra/${encodeURIComponent(codigo)}/lead-time-override`,
                { ...overrides, fornecedor, loja }
            );
            return resp;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['sugestaoCompra', codigo] });
            setShowModal(false);
        }
    });

    // Não renderiza placeholder enquanto carrega — cache hit é instantâneo (~40ms).
    if (isLoading) return null;
    if (isError || !data?.success) return null;
    if (data.cache_miss || data.sugestao === null || data.sugestao === undefined) return null;

    const { sugestao, calculo, alertas = [] } = data;
    const ehZero = sugestao === 0;
    // 3 cenários (pessimista/mediana/otimista) — pode faltar em cache antigo (fallback p/ número único).
    const cen = calculo.cenarios || null;
    const todosZero = cen
        ? (cen.pessimista.sugestao === 0 && cen.mediana.sugestao === 0 && cen.otimista.sugestao === 0)
        : ehZero;
    const semCompra = todosZero;
    const algumOverride = calculo.lead_time_fonte === 'manual (usuário)' || calculo.cobertura_manual || calculo.buffer_manual;

    const abrirModal = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Fecha tooltip/detalhe do card que o parent renderiza no hover —
        // o mouse ainda está sobre o card quando o modal abre, então mouseLeave não dispara natural.
        onCloseTooltip?.();
        setShowModal(true);
    };

    return (
        <>
            <div style={{
                position: 'relative',
                marginTop: '8px',
                padding: '6px 8px',
                paddingBottom: '18px',
                background: semCompra ? '#f0fdf4' : '#eff6ff',
                border: `1px solid ${semCompra ? '#bbf7d0' : '#bfdbfe'}`,
                borderRadius: '4px'
            }}>
                <div style={{ fontSize: '0.6rem', fontWeight: 700, color: semCompra ? '#15803d' : '#1d4ed8', letterSpacing: '0.5px', marginBottom: '2px' }}>
                    SUGESTÃO DE COMPRA
                    {algumOverride && <span style={{ marginLeft: '4px', fontWeight: 400, fontStyle: 'italic', textTransform: 'none', letterSpacing: 0 }}>(editada)</span>}
                </div>
                {!cen ? (
                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: semCompra ? '#15803d' : '#1d4ed8', lineHeight: 1 }}>
                        {todosZero ? 'Não comprar' : `${sugestao} un`}
                    </div>
                ) : (
                    <>
                        <div style={{ display: 'flex', gap: '3px', marginTop: '1px' }}>
                            {[
                                { key: 'pessimista', label: 'Pessimista', cor: '#dc2626' },
                                { key: 'mediana', label: 'Mediana', cor: '#a16207' },
                                { key: 'otimista', label: 'Otimista', cor: '#16a34a' }
                            ].map(({ key, label, cor }) => {
                                const v = cen[key]?.sugestao ?? 0;
                                const destaque = key === 'mediana';
                                // Cenário 0 continua visível (pedido 2026-07-01: os 3 sempre aparecem) — só esmaece
                                const corValor = v > 0 ? cor : '#94a3b8';
                                return (
                                    <div key={key} style={{
                                        flex: 1, textAlign: 'center', padding: '2px 1px', borderRadius: '4px',
                                        background: destaque ? '#fef9c3' : 'transparent',
                                        border: `1px solid ${destaque ? '#fde047' : 'transparent'}`
                                    }}>
                                        <div style={{ fontSize: '0.5rem', fontWeight: 700, color: cor, textTransform: 'uppercase', letterSpacing: '0.2px' }}>{label}</div>
                                        <div style={{ fontSize: destaque ? '1.15rem' : '0.9rem', fontWeight: 800, color: corValor, lineHeight: 1.15 }}>
                                            {v}
                                        </div>
                                        <div style={{ fontSize: '0.5rem', color: '#94a3b8', lineHeight: 1 }}>un</div>
                                    </div>
                                );
                            })}
                        </div>
                        {todosZero && (
                            <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#15803d', textAlign: 'center', marginTop: '2px' }}>
                                {cen.motivo_zerado === 'estoque_cobre'
                                    ? 'Estoque cobre 30 dias — não comprar'
                                    : 'Não comprar'}
                            </div>
                        )}
                    </>
                )}

                {/* Lápis no canto inferior direito → abre modal */}
                <button
                    onClick={abrirModal}
                    title="Editar fórmula da sugestão"
                    style={{
                        position: 'absolute',
                        bottom: '4px',
                        right: '6px',
                        background: 'rgba(255,255,255,0.7)',
                        border: '1px solid #cbd5e1',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        color: '#475569',
                        padding: '3px 6px',
                        borderRadius: '4px',
                        lineHeight: 1,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
                    }}
                >
                    <i className="fas fa-pencil-alt"></i>
                </button>
            </div>

            <SugestaoCompraModal
                show={showModal}
                onHide={() => setShowModal(false)}
                codigo={codigo}
                descricao={descricao}
                calculo={calculo}
                solicitacoes={data.solicitacoes_lista || []}
                onSalvar={mutation.mutate}
                saving={mutation.isPending}
                errorMsg={mutation.isError ? 'Erro ao salvar — tenta de novo' : null}
            />
        </>
    );
};

// Opções de informação disponíveis
const INFORMACAO_OPTIONS = ['recorrente', 'nao_comprar', 'promocao', 'novo', 'destaque', 'baixa_rotacao'];

const normalizeInfo = (info) => {
    if (!info || typeof info !== 'string') return null;
    const trimmed = info.trim();
    if (!trimmed) return null;

    const lower = trimmed.toLowerCase();
    if (lower === 'recorrente') return 'recorrente';
    if (lower === 'nao_comprar' || lower === 'não comprar' || lower === 'nao comprar') return 'nao_comprar';
    if (lower === 'promocao' || lower === 'promoção') return 'promocao';
    if (lower === 'novo') return 'novo';
    if (lower === 'destaque') return 'destaque';
    if (lower === 'baixa_rotacao' || lower === 'baixa rotação' || lower === 'baixa rotacao') return 'baixa_rotacao';

    return trimmed
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
};

const getInfoLabel = (info) => {
    switch (info) {
        case 'recorrente': return 'Recorrente';
        case 'nao_comprar': return 'Não Comprar';
        case 'promocao': return 'Promoção';
        case 'novo': return 'Novo';
        case 'destaque': return 'Destaque';
        case 'baixa_rotacao': return 'Analise';
        default: return info;
    }
};

// Cores para cada tipo de informação
const getInfoColor = (info) => {
    const colors = {
        'recorrente': 'primary',
        'nao_comprar': 'danger',
        'promocao': 'warning',
        'novo': 'success',
        'destaque': 'info',
        'baixa_rotacao': 'secondary'
    };
    return colors[info] || 'secondary';
};

const ProductCard = ({ produto, tipo, lojaContexto, onOpenTaskModal, onOpenInfoModal, onMouseEnter, onMouseLeave, hasPendingTasks, modoListaCompras, noCarrinho, qtdNoCarrinho }) => {
    const isEcommerce = tipo === 'ecommerce' || tipo === 'comparacao';
    const isComparacao = tipo === 'comparacao';
    const [showInfoDropdown, setShowInfoDropdown] = useState(false);
    const [precoPopover, setPrecoPopover] = useState(null);

    const formatarMoeda = (valor) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor || 0);
    };

    const getImageSrc = () => {
        if (isEcommerce) return produto.imagemURL;
        if (produto.imagemBase64) return produto.imagemBase64;
        return null;
    };

    const getNome = () => isEcommerce ? produto.nome : produto.descricao;
    const getCodigo = () => isEcommerce ? produto.codigo : produto.codigoInterno;
    const getPrecoVenda = () => isEcommerce ? produto.preco : produto.precoVenda;

    const estoque = produto.estoque || 0;
    const vendasMes = produto.vendasMesAtual || 0;
    const filtroPeriodo = produto.filtroPeriodo || false;

    // Processar informações do produto
    const getInformacoes = () => {
        if (!produto.informacao) return [];
        if (Array.isArray(produto.informacao)) return produto.informacao;
        if (typeof produto.informacao === 'string') {
            try {
                const parsed = JSON.parse(produto.informacao);
                if (Array.isArray(parsed)) return parsed;
                // Se parseou mas não é array (ex: string "promocao" dentro de aspas), retorna array
                return [parsed];
            } catch {
                // Se falhou parse, retorna a string crua
                return [produto.informacao];
            }
        }
        return [];
    };

    const informacoes = getInformacoes();
    // Remover duplicatas e valores vazios
    const informacoesUnicas = [...new Set(informacoes.filter(Boolean))];

    const getBadgeLabel = (info) => {
        // Tenta mapear nomes conhecidos, se não usa o próprio texto
        const map = {
            'recorrente': 'Recorrente',
            'nao_comprar': 'Não Comprar',
            'promocao': 'Promoção',
            'novo': 'Novo',
            'destaque': 'Destaque',
            'baixa_rotacao': 'Analise'
        };
        // Logica para tentar achar case-insensitive
        const key = Object.keys(map).find(k => k.toLowerCase() === info.toLowerCase());
        return key ? map[key] : info;
    };

    const getBadgeColor = (info) => {
        const colors = {
            'recorrente': 'primary',
            'nao_comprar': 'danger',
            'promocao': 'warning',
            'novo': 'success',
            'destaque': 'info',
            'baixa_rotacao': 'secondary'
        };
        // Tenta achar por ID exato ou case-insensitive
        const key = Object.keys(colors).find(k => k.toLowerCase() === info.toLowerCase());
        return key ? colors[key] : 'secondary'; // Fallback para secondary (cinza)
    };

    const [copied, setCopied] = useState(false);
    const [barrasCopied, setBarrasCopied] = useState(false);

    const handleCopyCode = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const codigo = getCodigo();
        if (codigo) {
            navigator.clipboard.writeText(codigo);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleCopyBarras = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (produto.codigoBarras) {
            navigator.clipboard.writeText(produto.codigoBarras);
            setBarrasCopied(true);
            setTimeout(() => setBarrasCopied(false), 2000);
        }
    };

    return (
        <div
            className={styles.card}
            onMouseEnter={onMouseEnter}
            onMouseLeave={() => { onMouseLeave?.(); setPrecoPopover(null); }}
            style={modoListaCompras ? {
                outline: noCarrinho ? '2.5px solid #22c55e' : '2px dashed rgba(29, 78, 216, 0.4)',
                boxShadow: noCarrinho ? '0 0 0 4px rgba(34,197,94,0.12)' : undefined,
                transition: 'all 0.2s',
            } : {}}
        >
            {/* Badge "na lista" quando modo ativo */}
            {modoListaCompras && noCarrinho && (
                <div style={{
                    position: 'absolute',
                    top: '6px',
                    left: '6px',
                    background: 'linear-gradient(135deg, #16a34a, #15803d)',
                    color: '#fff',
                    borderRadius: '20px',
                    padding: '2px 10px',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    zIndex: 10,
                    boxShadow: '0 2px 8px rgba(22,163,74,0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                }}>
                    ✓ {qtdNoCarrinho} {qtdNoCarrinho === 1 ? 'dz' : 'dz'}
                </div>
            )}
            <div className={styles.imageContainer}>
                <ProductImage
                    src={getImageSrc()}
                    alt={getNome()}
                    className={styles.image}
                />

                {/* Tags de informação - lado direito */}
                {informacoesUnicas.length > 0 && (
                    <div className={styles.productInfoBadges}>
                        {informacoesUnicas.map((info, index) => (
                            <Badge
                                key={index}
                                bg={getBadgeColor(info)}
                                className="shadow-sm"
                                style={{
                                    marginBottom: '2px',
                                    textTransform: 'capitalize',
                                    fontWeight: 'normal',
                                    letterSpacing: '0.3px'
                                }}
                            >
                                {getBadgeLabel(info)}
                            </Badge>
                        ))}
                    </div>
                )}

                {/* Badges de vendas/estoque - lado esquerdo (oculto no modo comparacao) */}
                {!isComparacao && (
                    <div className={styles.salesBadges}>
                        {vendasMes > 0 && (
                            <Badge bg={filtroPeriodo ? "success" : "info"} className="shadow-sm">
                                {vendasMes} {filtroPeriodo ? 'no período' : 'vendas'}
                            </Badge>
                        )}
                        {estoque > 0 && (
                            <Badge bg="light" text="dark" className="shadow-sm border">
                                Estoque: {estoque}
                            </Badge>
                        )}
                        {estoque <= 0 && (
                            <Badge bg="danger" className="shadow-sm">
                                Sem Estoque
                            </Badge>
                        )}
                    </div>
                )}
            </div>

            <div className={styles.body}>
                <div className="d-flex justify-content-between align-items-start">
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {produto.codigoBarras ? (
                            <div
                                className={`${styles.code} ${barrasCopied ? styles.codeSuccess : ''}`}
                                onClick={handleCopyBarras}
                                title={`Código completo: ${produto.codigoBarras} — Clique para copiar`}
                            >
                                <i className="fas fa-barcode me-1" style={{ opacity: 0.7 }}></i>
                                #{String(produto.codigoBarras).replace(/^0+/, '') || '0'}
                                {barrasCopied && <i className="fas fa-check ms-1"></i>}
                            </div>
                        ) : (
                            <div
                                className={`${styles.code} ${copied ? styles.codeSuccess : ''}`}
                                onClick={handleCopyCode}
                                title="Clique para copiar código"
                            >
                                #{getCodigo()}
                                {copied && <i className="fas fa-check ms-1"></i>}
                            </div>
                        )}
                        {isComparacao && produto.marca && (
                            <div className="text-warning small fw-bold mt-1" style={{ fontSize: '0.7rem' }}>
                                MARCA: {produto.marca}
                            </div>
                        )}
                    </div>
                    {produto.fornecedor && (
                        <div
                            className="text-muted text-truncate ms-2"
                            style={{ fontSize: '0.7rem', maxWidth: '65%', textAlign: 'right' }}
                            title={`Fornecedor: ${produto.fornecedor}`}
                        >
                            <i className="fas fa-truck me-1" style={{ fontSize: '0.6rem' }}></i>
                            {produto.fornecedor}
                        </div>
                    )}
                </div>
                <div className={styles.title} title={getNome()}>
                    {getNome()}
                </div>

                {/* Vendas CT / CX / BL — apenas no catálogo de comparação */}
                {isComparacao && (
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px', marginBottom: '2px' }}>
                        <Badge bg={produto.vinculadoLocal ? "success" : "secondary"} style={{ fontSize: '0.68rem' }}>
                            <i className="fas fa-store me-1"></i>CT: {produto.vendasCentro || 0}v / {produto.estoqueCentro || 0}
                        </Badge>
                        <Badge bg={produto.vinculadoLocal ? "info" : "secondary"} style={{ fontSize: '0.68rem' }}>
                            <i className="fas fa-building me-1"></i>CX: {produto.vendasCaxias || 0}v / {produto.estoqueCaxias || 0}
                        </Badge>
                        <Badge bg="warning" text="dark" style={{ fontSize: '0.68rem' }}>
                            <i className="fas fa-globe me-1"></i>BL: {produto.vendasEcommerce || 0}v / {produto.estoqueEcommerce || 0}
                        </Badge>
                    </div>
                )}




                <div className={styles.priceRow}>
                    <div className={styles.price} style={{ display: 'flex', alignItems: 'center', gap: '3px', position: 'relative' }}>
                        {formatarMoeda(getPrecoVenda())}
                        {produto.historicoPreco?.venda && (() => {
                            const h = produto.historicoPreco.venda;
                            const subiu = h.precoNovo > h.precoAnterior;
                            return (
                                <span
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPrecoPopover(precoPopover === 'venda' ? null : 'venda'); }}
                                    title="Preço alterado — clique para ver"
                                    style={{
                                        cursor: 'pointer',
                                        fontSize: '0.6rem',
                                        color: subiu ? '#ef4444' : '#22c55e',
                                        fontWeight: 800,
                                        lineHeight: 1,
                                        marginLeft: '1px',
                                    }}
                                >{subiu ? '▲' : '▼'}</span>
                            );
                        })()}
                        {precoPopover === 'venda' && produto.historicoPreco?.venda && (
                            <div style={{
                                position: 'absolute', top: '100%', left: 0, zIndex: 50,
                                background: '#1e293b', border: '1px solid rgba(255,255,255,0.15)',
                                borderRadius: '8px', padding: '8px 12px', minWidth: '170px',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.5)', fontSize: '0.72rem',
                            }} onClick={(e) => e.stopPropagation()}>
                                <div style={{ color: '#94a3b8', marginBottom: '4px' }}>Preço de Venda</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                                    <span style={{ color: '#64748b', textDecoration: 'line-through' }}>{formatarMoeda(produto.historicoPreco.venda.precoAnterior)}</span>
                                    <span style={{ color: '#fff', fontWeight: 700 }}>→ {formatarMoeda(produto.historicoPreco.venda.precoNovo)}</span>
                                </div>
                                <div style={{ color: '#64748b', fontSize: '0.65rem', marginTop: '4px' }}>
                                    📅 {new Date(produto.historicoPreco.venda.dataAlteracao).toLocaleDateString('pt-BR')}
                                </div>
                            </div>
                        )}
                    </div>
                    {produto.precoCusto > 0 ? (
                        <div className={styles.cost} title="Preço de Custo" style={{ display: 'flex', alignItems: 'center', gap: '3px', position: 'relative' }}>
                            {formatarMoeda(produto.precoCusto)}
                            {produto.historicoPreco?.custo && (() => {
                                const h = produto.historicoPreco.custo;
                                const subiu = h.precoNovo > h.precoAnterior;
                                return (
                                    <span
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPrecoPopover(precoPopover === 'custo' ? null : 'custo'); }}
                                        title="Custo alterado — clique para ver"
                                        style={{
                                            cursor: 'pointer',
                                            fontSize: '0.6rem',
                                            color: subiu ? '#ef4444' : '#22c55e',
                                            fontWeight: 800,
                                            lineHeight: 1,
                                            marginLeft: '1px',
                                        }}
                                    >{subiu ? '▲' : '▼'}</span>
                                );
                            })()}
                            {precoPopover === 'custo' && produto.historicoPreco?.custo && (
                                <div style={{
                                    position: 'absolute', top: '100%', right: 0, zIndex: 50,
                                    background: '#1e293b', border: '1px solid rgba(255,255,255,0.15)',
                                    borderRadius: '8px', padding: '8px 12px', minWidth: '170px',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.5)', fontSize: '0.72rem',
                                }} onClick={(e) => e.stopPropagation()}>
                                    <div style={{ color: '#94a3b8', marginBottom: '4px' }}>Preço de Custo</div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                                        <span style={{ color: '#64748b', textDecoration: 'line-through' }}>{formatarMoeda(produto.historicoPreco.custo.precoAnterior)}</span>
                                        <span style={{ color: '#fff', fontWeight: 700 }}>→ {formatarMoeda(produto.historicoPreco.custo.precoNovo)}</span>
                                    </div>
                                    <div style={{ color: '#64748b', fontSize: '0.65rem', marginTop: '4px' }}>
                                        📅 {new Date(produto.historicoPreco.custo.dataAlteracao).toLocaleDateString('pt-BR')}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={styles.cost} title="Preço de Custo não cadastrado" style={{ color: '#e74c3c', fontWeight: 'bold' }}>
                            R$ 0,00
                        </div>
                    )}
                </div>

                <div className={styles.statsRow}>
                    <div className={styles.statItem}>
                        <span className={styles.statLabel}>Média Dia</span>
                        <span className={styles.statValue}>
                            {produto.mediaDiariaAjustada > 0 ? produto.mediaDiariaAjustada.toFixed(1) : '0.0'}
                        </span>
                    </div>
                    <div className={styles.statItem}>
                        <span className={styles.statLabel}>Média Mês</span>
                        <span className={styles.statValue}>
                            {produto.mediaMensal ? Math.round(produto.mediaMensal) : 0}
                        </span>
                    </div>
                    <div className={styles.statItem}>
                        <span className={styles.statLabel}>{produto.qtdChegada !== undefined ? 'Chegou' : 'Markup'}</span>
                        <span className={`${styles.statValue} ${(() => {
                            if (produto.qtdChegada !== undefined) return '';
                            const precoVenda = isEcommerce ? produto.preco : produto.precoVenda;
                            const precoCusto = produto.precoCusto || 0;
                            if (precoCusto <= 0) return 'text-muted';
                            const markup = ((precoVenda - precoCusto) / precoCusto) * 100;
                            return markup >= 50 ? 'text-success' : markup >= 20 ? 'text-warning' : 'text-danger';
                        })()}`}>
                            {produto.qtdChegada !== undefined ? produto.qtdChegada : (() => {
                                const precoVenda = isEcommerce ? produto.preco : produto.precoVenda;
                                const precoCusto = produto.precoCusto || 0;
                                if (precoCusto <= 0) return '-';
                                const markup = ((precoVenda - precoCusto) / precoCusto) * 100;
                                return `${markup.toFixed(0)}%`;
                            })()}
                        </span>
                    </div>
                </div>

                <SugestaoCompraBloco
                    codigo={getCodigo()}
                    descricao={getNome()}
                    fornecedor={produto.fornecedor}
                    loja={lojaContexto || (isEcommerce ? 'ecommerce' : 'centro')}
                    estoque={estoque}
                    precoCusto={produto.precoCusto}
                    precoVenda={getPrecoVenda()}
                    onCloseTooltip={onMouseLeave}
                />
            </div>

            <div className={styles.actions}>
                <Link to={`/${tipo}/${getCodigo()}${!isEcommerce && lojaContexto ? `?loja=${lojaContexto}` : ''}`} className={styles.actionBtn}>
                    <i className="fas fa-eye me-1"></i> Detalhes
                </Link>
                <button
                    className={`${styles.actionBtn} ${styles.infoBtn}`}
                    onClick={() => onOpenInfoModal && onOpenInfoModal(produto)}
                    title="Adicionar informação"
                >
                    <i className="fas fa-tag me-1"></i> Info
                </button>
                <button
                    className={`${styles.actionBtn} ${hasPendingTasks ? styles.obsBtnAlert : ''}`}
                    onClick={() => onOpenTaskModal && onOpenTaskModal(produto)}
                    title={hasPendingTasks ? "Observações pendentes!" : "Adicionar observação"}
                >
                    <i className={`fas ${hasPendingTasks ? 'fa-exclamation-circle' : 'fa-tasks'} me-1`}></i> Obs
                </button>
            </div>
        </div>
    );
};

export default ProductCard;
